<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content>
        <h1 class="tw-text-2xl sm:tw-text-3xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">My Documents</h1>
      </base-content>
    </div>
    <base-content>
      <base-table :fields="fields" :items="items" :loading="isLoading">
        <template #document="{ item: document }">
          <div class="tw-flex tw-items-center">
            <component class="tw-h-5" :is="getIcon(document)" :class="getIconClass(document)"></component>
            <div class="tw-ml-1.5">
              <div class="tw-flex tw-items-center">
                {{ document.name }}
              </div>
              <div class="tw-text-xs tw-text-gray-500">{{ getDocumentTypeDisplayName(document.type) }}</div>
            </div>
          </div>
        </template>
        <template #uploaded="{ item: document }">
          {{ formatDate(document.insertDate) }}
        </template>
        <template #download="{ item: document }">
          <div class="tw-flex tw-items-center tw-justify-end">
            <base-icon @click="showDocument(document)" v-if="VIEWABLE_EXTENSIONS.includes(document.extension)" class="tw-mr-2">
              <eye-icon class="tw-w-5 tw-h-5" />
            </base-icon>
            <base-icon @click="download(document.documentId)" :loading="loadingDocumentId === document.documentId">
              <download-icon class="tw-w-5 tw-h-5" />
            </base-icon>
          </div>
        </template>
        <template #loading-row>
          <td class="tw-py-3 tw-px-4 tw-flex tw-items-center">
            <base-skeleton-loader class="tw-h-4 tw-w-4" />
            <div class="tw-ml-2.5">
              <base-skeleton-loader class="tw-h-3 tw-w-48 tw-mt-1" />
              <base-skeleton-loader class="tw-h-2 tw-w-24 tw-mt-3 tw-mb-1" />
            </div>
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-20" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-7 tw-w-7 tw-rounded-full" />
          </td>
        </template>
        <template #empty-state>
          <div class="tw-flex tw-justify-center tw-py-5">No Documents</div>
        </template>
      </base-table>
      <base-pagination v-model="page" class="tw-mt-2" />
    </base-content>
    <document-modal ref="documentModal" />
  </div>
</template>

<script>
import DocumentModal from "@/components/DocumentModal";
import { DownloadIcon, EyeIcon, PdfIcon, PngIcon, JpgIcon, MsWordIcon, MsExcelIcon, MsPowerpointIcon, DocumentIcon } from "@/assets/svgs";
import moment from "moment";
import { VIEWABLE_EXTENSIONS } from "@/constants/constants";
import _ from "lodash";

export default {
  components: {
    DocumentModal,
    DownloadIcon,
    EyeIcon,
    PdfIcon,
    PngIcon,
    JpgIcon,
    MsWordIcon,
    MsPowerpointIcon,
    MsExcelIcon,
    DocumentIcon,
  },
  props: {
    userId: { type: String, required: true },
  },
  data() {
    return {
      isLoading: false,
      loadingDocumentId: null,
      fields: [{ title: "Document", key: "document" }, { title: "Uploaded", key: "uploaded" }, { title: "", key: "download", thClass: "tw-w-0 tw-text-center" }],
      page: {
        skip: 0,
        take: 10,
        total: 0,
      },
      items: [],
      VIEWABLE_EXTENSIONS,
    };
  },
  created() {
    this.loadDocuments();
  },
  methods: {
    async loadDocuments() {
      try {
        this.isLoading = true;
        const response = await this.$axios.get(`/users/${this.userId}/documents?skip=${this.page.skip}&take=${this.page.take}`);
        this.items = response.data.items;
        this.page.take = response.data.take;
        this.page.skip = response.data.skip;
        this.page.total = response.data.total;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load documents",
          message: "We had a problem retrieving the list of documents. Please reload the page to try again.",
          error: error,
        });
      } finally {
        this.isLoading = false;
      }
    },
    showDocument(document) {
      this.$refs.documentModal.show(document);
    },
    getDocumentTypeDisplayName(type) {
      return _.startCase(_.toLower(type));
    },
    getIcon(document) {
      switch (document.extension) {
        case "pdf":
          return "PdfIcon";
        case "png":
          return "PngIcon";
        case "jpeg":
          return "JpgIcon";
        case "docx":
          return "MsWordIcon";
        case "pptx":
          return "MsPowerpointIcon";
        case "xlsx":
          return "MsExcelIcon";
        default:
          return "DocumentIcon";
      }
    },
    getIconClass(document) {
      switch (document.extension) {
        case "pdf":
          return "pdf-icon";
        case "png":
          return "png-icon";
        case "jpeg":
          return "jpeg-icon";
        case "docx":
          return "word-icon";
        case "pptx":
          return "ppt-icon";
        default:
          return "file";
      }
    },
    formatDate(date) {
      return new moment.utc(date).local().format("MMMM Do, YYYY h:mm A");
    },
    async download(documentId) {
      try {
        this.loadingDocumentId = documentId;
        const documentResponse = await this.$axios.get(`/documents/${documentId}`);
        const downloadResponse = await this.$axios.get(documentResponse.data.downloadUrl, { responseType: "blob" });
        var blob = new Blob([downloadResponse.data]);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = documentResponse.data.name + "." + documentResponse.data.extension;
        link.click();
        URL.revokeObjectURL(link.href);
      } finally {
        this.loadingDocumentId = null;
      }
    },
  },
  watch: {
    page: {
      handler() {
        this.loadDocuments();
      },
    },
  },
};
</script>
